import React from 'react';
import { motion } from 'framer-motion';
import { Award, Languages, Heart, Mail, Download } from 'lucide-react';
import { Github, Linkedin, Twitter } from 'lucide-react';

const EducationCard = ({ education }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="bg-gradient-to-br from-[#101C10]/40 to-[#112811]/40 backdrop-blur-sm 
               rounded-2xl p-4 sm:p-6 border border-[#2fe22f]/10 hover:border-[#2fe22f]/30 
               transition-all duration-500"
  >
    <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0">
      <div className="space-y-2 w-full sm:w-auto">
        <h3 className="text-lg sm:text-xl font-bold text-[#2fe22f]">{education.degree}</h3>
        <p className="text-gray-300 text-sm sm:text-base">{education.college}</p>
      </div>
      <div className="text-left sm:text-right w-full sm:w-auto">
        <p className="text-gray-300 text-sm sm:text-base">{education.year}</p>
        <p className="text-[#2fe22f] text-sm sm:text-base">{education.gpa}</p>
      </div>
    </div>
  </motion.div>
);

const ListSection = ({ title, items, icon: Icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="bg-gradient-to-br from-[#101C10]/40 to-[#112811]/40 backdrop-blur-sm 
               rounded-2xl p-4 sm:p-6 border border-[#2fe22f]/10 hover:border-[#2fe22f]/30 
               transition-all duration-500 h-full"
  >
    <div className="flex items-center gap-3 mb-4">
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#2fe22f]" />
      <h3 className="text-lg sm:text-xl font-bold text-[#2fe22f]">{title}</h3>
    </div>
    <ul className="space-y-3">
      {items.map((item, index) => (
        <motion.li
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex items-start gap-3 text-gray-300 text-sm sm:text-base"
        >
          <div className="mt-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#2fe22f]" />
          </div>
          {item}
        </motion.li>
      ))}
    </ul>
  </motion.div>
);

const ContactSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="bg-gradient-to-br from-[#101C10]/40 to-[#112811]/40 backdrop-blur-sm 
               rounded-2xl p-6 sm:p-8 border border-[#2fe22f]/10 hover:border-[#2fe22f]/30 
               transition-all duration-500 text-center"
  >
    <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8">
      Want to collaborate, ask something or chat? Be sure to contact me through any of my social media. 
      You can also drop me an email and I will try to reply as soon as possible.
    </p>
    <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        href="mailto:alroypereira05@gmail.com"
        className="p-3 bg-[#2fe22f]/10 rounded-full text-[#2fe22f] hover:bg-[#2fe22f]/20 transition-colors"
      >
        <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.a>
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        href="https://www.linkedin.com/in/alroy-pereira-2b3631248/"
        target="_blank"
        rel="noopener noreferrer"
        className="p-3 bg-[#2fe22f]/10 rounded-full text-[#2fe22f] hover:bg-[#2fe22f]/20 transition-colors"
      >
        <Linkedin className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.a>
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        href="https://github.com/Alroy05"
        target="_blank"
        rel="noopener noreferrer"
        className="p-3 bg-[#2fe22f]/10 rounded-full text-[#2fe22f] hover:bg-[#2fe22f]/20 transition-colors"
      >
        <Github className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.a>
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        href="https://x.com/AlroyPereira3"
        target="_blank"
        rel="noopener noreferrer"
        className="p-3 bg-[#2fe22f]/10 rounded-full text-[#2fe22f] hover:bg-[#2fe22f]/20 transition-colors"
      >
        <Twitter className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.a>
      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        href="/"
        download="pdf"
        className="p-3 bg-[#2fe22f]/10 rounded-full text-[#2fe22f] hover:bg-[#2fe22f]/20 transition-colors"
      >
        <div className='flex flex-row items-center gap-2 mx-2 text-sm sm:text-base'>
          <Download className="w-5 h-5 sm:w-6 sm:h-6" /> 
          <span>Download Resume</span>
        </div>
      </motion.a>
    </div>
  </motion.div>
);

const AdditionalSections = () => {
  const education = [
    {
      degree: "Bachelor of Engineering in Computer Science",
      college: "Fr. Conceicao Rodrigues College of Engineering",
      year: "2021 - 2025",
      gpa: "CGPA: 9.06"
    },
    {
      degree: "HSC",
      college: "R.P. Junior College (IIT's PACE)",
      year: "2019 - 2021",
      gpa: "Percentage: 95%"
    },
    {
      degree: "SSC",
      college: "St. Augustine's High School",
      year: "2019",
      gpa: "Percentage: 90%"
    }
  ];

  const achievements = [
    "Smart India Hackathon Winner 23 (Software Edition) - Ministry of Law and Justice",
    "Innovation, Design and Entrepreneurship (IDE) , Idea competition Rank 5",
    "Hackover 3.0 Hackathon Rank 7",
    "Conferred scholarship of INR 400000 under INSPIRE for higher studies by performing within the top 1% in XIIth board exam"
  ];

  const languages = [
    "English - Full Professional proficiency",
    "Hindi - Full Professional proficiency",
    "Marathi - Full Professional proficiency",
  ];

  const volunteering = [
    "Volunteer NSS CRCE (Aug 2022 - May 2023)"
  ];

  return (
    <section className="w-full font-lato bg-gradient-to-br from-[#101C10] to-[#112811] py-12 sm:py-20" id='more info'>
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl space-y-12 sm:space-y-20">
        {/* Education Section */}
        <div className="space-y-6 sm:space-y-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-8 sm:w-12 h-1 bg-[#2fe22f]" 
            />
            <h2 className="text-2xl sm:text-4xl font-bold text-[#2fe22f]">Education</h2>
          </div>
          <div className="space-y-4 sm:space-y-6">
            {education.map((edu, index) => (
              <EducationCard key={index} education={edu} />
            ))}
          </div>
        </div>

        {/* Lists Sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <ListSection title="Achievements" items={achievements} icon={Award} />
          <ListSection title="Languages" items={languages} icon={Languages} />
          <ListSection title="Volunteering" items={volunteering} icon={Heart} />
        </div>

        {/* Contact Section */}
        <div className="space-y-6 sm:space-y-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-8 sm:w-12 h-1 bg-[#2fe22f]" 
            />
            <h2 className="text-2xl sm:text-4xl font-bold text-[#2fe22f]">Get in Touch</h2>
          </div>
          <div id='contact'>
            <ContactSection />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdditionalSections;